import { supabase } from './supabase'

// ============================================
// WORK ORDERS HELPERS
// ============================================

/**
 * Create a new work order
 * @param {Object} data - Work order data
 * @param {string} data.clientId - UUID of the client creating the order
 * @param {string} [data.vehicleId] - UUID of associated vehicle
 * @param {string} data.type - Type of work order
 * @param {string} data.title - Work order title
 * @param {string} [data.description] - Detailed description
 * @param {string} [data.aiSummary] - AI's context/summary
 * @param {string} [data.status] - Status (default: 'pending')
 * @param {string} [data.priority] - Priority level (default: 'normal')
 * @param {string} [data.assignedTo] - UUID of assigned admin/staff
 * @param {string} [data.providerId] - Service provider ID
 * @param {string} [data.providerName] - Service provider name
 * @param {string} [data.providerPhone] - Service provider phone
 * @param {string} [data.notes] - Admin notes
 * @returns {Promise<{data, error}>}
 */
export const createWorkOrder = async (data) => {
  const { data: result, error } = await supabase
    .from('work_orders')
    .insert({
      client_id: data.clientId,
      vehicle_id: data.vehicleId || null,
      type: data.type,
      title: data.title,
      description: data.description || null,
      ai_summary: data.aiSummary || null,
      status: data.status || 'pending',
      priority: data.priority || 'normal',
      assigned_to: data.assignedTo || null,
      provider_id: data.providerId || null,
      provider_name: data.providerName || null,
      provider_phone: data.providerPhone || null,
      notes: data.notes || null
    })
    .select()
    .single()
  return { data: result, error }
}

/**
 * Get work orders for a client or all if admin
 * @param {string} clientId - UUID of client to filter by (omit for all)
 * @param {Object} [options] - Query options
 * @param {string} [options.status] - Filter by status
 * @param {string} [options.priority] - Filter by priority
 * @param {string} [options.type] - Filter by type
 * @param {number} [options.limit] - Limit results
 * @returns {Promise<{data, error}>}
 */
export const getWorkOrders = async (clientId = null, options = {}) => {
  let query = supabase
    .from('work_orders')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model)
    `)
    .order('created_at', { ascending: false })

  if (clientId) {
    query = query.eq('client_id', clientId)
  }

  if (options.status) {
    query = query.eq('status', options.status)
  }

  if (options.priority) {
    query = query.eq('priority', options.priority)
  }

  if (options.type) {
    query = query.eq('type', options.type)
  }

  if (options.limit) {
    query = query.limit(options.limit)
  }

  const { data, error } = await query
  if (error || !data) return { data, error }

  // Fetch client profiles separately (work_orders FK is to auth.users, not profiles)
  const clientIds = [...new Set(data.map(w => w.client_id).filter(Boolean))]
  const assignedIds = [...new Set(data.map(w => w.assigned_to).filter(Boolean))]
  const allProfileIds = [...new Set([...clientIds, ...assignedIds])]

  let profilesMap = {}
  if (allProfileIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', allProfileIds)
    if (profiles) {
      profiles.forEach(p => { profilesMap[p.id] = p })
    }
  }

  // Attach client and assigned_to_user to each work order
  const enriched = data.map(w => ({
    ...w,
    client: profilesMap[w.client_id] || null,
    assigned_to_user: profilesMap[w.assigned_to] || null
  }))

  return { data: enriched, error: null }
}

/**
 * Get a specific work order by ID
 * @param {string} id - Work order UUID
 * @returns {Promise<{data, error}>}
 */
export const getWorkOrder = async (id) => {
  const { data, error } = await supabase
    .from('work_orders')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model),
      chat_sessions:ai_chat_sessions(id, title),
      phone_logs:phone_call_logs(id, status, created_at)
    `)
    .eq('id', id)
    .single()

  if (error || !data) return { data, error }

  // Fetch client and assigned profiles separately
  const profileIds = [data.client_id, data.assigned_to].filter(Boolean)
  let profilesMap = {}
  if (profileIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', profileIds)
    if (profiles) {
      profiles.forEach(p => { profilesMap[p.id] = p })
    }
  }

  return {
    data: {
      ...data,
      client: profilesMap[data.client_id] || null,
      assigned_to_user: profilesMap[data.assigned_to] || null
    },
    error: null
  }
}

/**
 * Update a work order
 * @param {string} id - Work order UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updateWorkOrder = async (id, updates) => {
  const updateData = {}

  // Map camelCase to snake_case
  if (updates.status !== undefined) updateData.status = updates.status
  if (updates.priority !== undefined) updateData.priority = updates.priority
  if (updates.description !== undefined) updateData.description = updates.description
  if (updates.aiSummary !== undefined) updateData.ai_summary = updates.aiSummary
  if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo
  if (updates.providerId !== undefined) updateData.provider_id = updates.providerId
  if (updates.providerName !== undefined) updateData.provider_name = updates.providerName
  if (updates.providerPhone !== undefined) updateData.provider_phone = updates.providerPhone
  if (updates.notes !== undefined) updateData.notes = updates.notes
  if (updates.completedAt !== undefined) updateData.completed_at = updates.completedAt

  const { data, error } = await supabase
    .from('work_orders')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

/**
 * Get work order statistics for admin dashboard
 * @returns {Promise<{data, error}>}
 */
export const getWorkOrderStats = async () => {
  const { data, error } = await supabase
    .from('work_orders')
    .select('status, priority, type')

  if (error) return { data: null, error }

  const stats = {
    total: data.length,
    byStatus: {},
    byPriority: {},
    byType: {}
  }

  // Count by status
  const statuses = ['pending', 'in_progress', 'completed', 'cancelled']
  statuses.forEach(status => {
    stats.byStatus[status] = data.filter(w => w.status === status).length
  })

  // Count by priority
  const priorities = ['low', 'normal', 'high', 'urgent']
  priorities.forEach(priority => {
    stats.byPriority[priority] = data.filter(w => w.priority === priority).length
  })

  // Count by type
  const types = ['service_inquiry', 'transport', 'pickup_delivery', 'maintenance', 'storage', 'other']
  types.forEach(type => {
    stats.byType[type] = data.filter(w => w.type === type).length
  })

  return { data: stats, error: null }
}

// ============================================
// AI CHAT SESSIONS HELPERS
// ============================================

/**
 * Create a new AI chat session
 * @param {string} clientId - UUID of the client
 * @param {string} [vehicleId] - UUID of associated vehicle
 * @param {string} [title] - Session title
 * @returns {Promise<{data, error}>}
 */
export const createChatSession = async (clientId, vehicleId = null, title = null) => {
  const { data, error } = await supabase
    .from('ai_chat_sessions')
    .insert({
      client_id: clientId,
      vehicle_id: vehicleId,
      title: title
    })
    .select()
    .single()
  return { data, error }
}

/**
 * Get all chat sessions for a client
 * @param {string} clientId - UUID of the client
 * @param {Object} [options] - Query options
 * @param {number} [options.limit] - Limit results
 * @returns {Promise<{data, error}>}
 */
export const getChatSessions = async (clientId, options = {}) => {
  let query = supabase
    .from('ai_chat_sessions')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model),
      messages:ai_chat_messages(id)
    `)
    .eq('client_id', clientId)
    .order('updated_at', { ascending: false })

  if (options.limit) {
    query = query.limit(options.limit)
  }

  const { data, error } = await query
  return { data, error }
}

/**
 * Get a specific chat session
 * @param {string} sessionId - UUID of the chat session
 * @returns {Promise<{data, error}>}
 */
export const getChatSession = async (sessionId) => {
  const { data, error } = await supabase
    .from('ai_chat_sessions')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model),
      messages:ai_chat_messages(*)
    `)
    .eq('id', sessionId)
    .single()
  return { data, error }
}

/**
 * Update a chat session
 * @param {string} sessionId - UUID of the chat session
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updateChatSession = async (sessionId, updates) => {
  const { data, error } = await supabase
    .from('ai_chat_sessions')
    .update(updates)
    .eq('id', sessionId)
    .select()
    .single()
  return { data, error }
}

// ============================================
// AI CHAT MESSAGES HELPERS
// ============================================

/**
 * Get all messages for a chat session
 * @param {string} sessionId - UUID of the chat session
 * @returns {Promise<{data, error}>}
 */
export const getChatMessages = async (sessionId) => {
  const { data, error } = await supabase
    .from('ai_chat_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
  return { data, error }
}

/**
 * Add a message to a chat session
 * @param {string} sessionId - UUID of the chat session
 * @param {string} role - 'user', 'assistant', or 'system'
 * @param {string} content - Message content
 * @param {Object} [toolCalls] - Tool calls made by AI (if any)
 * @returns {Promise<{data, error}>}
 */
export const addChatMessage = async (sessionId, role, content, toolCalls = null) => {
  const { data, error } = await supabase
    .from('ai_chat_messages')
    .insert({
      session_id: sessionId,
      role,
      content,
      tool_calls: toolCalls
    })
    .select()
    .single()
  return { data, error }
}

/**
 * Get messages with a specific role from a session
 * @param {string} sessionId - UUID of the chat session
 * @param {string} role - 'user', 'assistant', or 'system'
 * @returns {Promise<{data, error}>}
 */
export const getMessagesByRole = async (sessionId, role) => {
  const { data, error } = await supabase
    .from('ai_chat_messages')
    .select('*')
    .eq('session_id', sessionId)
    .eq('role', role)
    .order('created_at', { ascending: true })
  return { data, error }
}

// ============================================
// PHONE CALL LOGS HELPERS
// ============================================

/**
 * Create a new phone call log
 * @param {Object} data - Phone call log data
 * @param {string} [data.workOrderId] - Associated work order UUID
 * @param {string} [data.sessionId] - Associated chat session UUID
 * @param {string} data.providerName - Name of service provider
 * @param {string} data.providerPhone - Phone number called
 * @param {string} [data.twilioCallSid] - Twilio call session ID
 * @param {string} [data.status] - Call status (default: 'initiated')
 * @param {string} [data.callPurpose] - Purpose of the call
 * @returns {Promise<{data, error}>}
 */
export const createPhoneCallLog = async (data) => {
  const { data: result, error } = await supabase
    .from('phone_call_logs')
    .insert({
      work_order_id: data.workOrderId || null,
      session_id: data.sessionId || null,
      provider_name: data.providerName,
      provider_phone: data.providerPhone,
      twilio_call_sid: data.twilioCallSid || null,
      status: data.status || 'initiated',
      call_purpose: data.callPurpose
    })
    .select()
    .single()
  return { data: result, error }
}

/**
 * Get phone call logs for a work order
 * @param {string} workOrderId - Work order UUID
 * @returns {Promise<{data, error}>}
 */
export const getPhoneCallLogsByWorkOrder = async (workOrderId) => {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select('*')
    .eq('work_order_id', workOrderId)
    .order('created_at', { ascending: false })
  return { data, error }
}

/**
 * Get phone call logs for a chat session
 * @param {string} sessionId - Chat session UUID
 * @returns {Promise<{data, error}>}
 */
export const getPhoneCallLogsBySession = async (sessionId) => {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false })
  return { data, error }
}

/**
 * Get a specific phone call log
 * @param {string} id - Phone call log UUID
 * @returns {Promise<{data, error}>}
 */
export const getPhoneCallLog = async (id) => {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select(`
      *,
      work_order:work_orders(id, title, type),
      session:ai_chat_sessions(id, title)
    `)
    .eq('id', id)
    .single()
  return { data, error }
}

/**
 * Update a phone call log
 * @param {string} id - Phone call log UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updatePhoneCallLog = async (id, updates) => {
  const updateData = {}

  // Map camelCase to snake_case
  if (updates.status !== undefined) updateData.status = updates.status
  if (updates.durationSeconds !== undefined) updateData.duration_seconds = updates.durationSeconds
  if (updates.recordingUrl !== undefined) updateData.recording_url = updates.recordingUrl
  if (updates.transcript !== undefined) updateData.transcript = updates.transcript
  if (updates.callResult !== undefined) updateData.call_result = updates.callResult
  if (updates.twilioCallSid !== undefined) updateData.twilio_call_sid = updates.twilioCallSid

  const { data, error } = await supabase
    .from('phone_call_logs')
    .update(updateData)
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

/**
 * Get recent phone calls (for activity feed)
 * @param {number} limit - Number of records to return
 * @returns {Promise<{data, error}>}
 */
export const getRecentPhoneCalls = async (limit = 10) => {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select(`
      *,
      work_order:work_orders(id, title),
      session:ai_chat_sessions(id, title)
    `)
    .order('created_at', { ascending: false })
    .limit(limit)
  return { data, error }
}

/**
 * Get call statistics for admin dashboard
 * @returns {Promise<{data, error}>}
 */
export const getPhoneCallStats = async () => {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select('status, duration_seconds')

  if (error) return { data: null, error }

  const stats = {
    total: data.length,
    byStatus: {},
    totalDuration: 0,
    averageDuration: 0
  }

  // Count by status
  const statuses = ['initiated', 'ringing', 'in_progress', 'completed', 'failed', 'no_answer', 'busy']
  statuses.forEach(status => {
    stats.byStatus[status] = data.filter(c => c.status === status).length
  })

  // Calculate duration stats
  const completedCalls = data.filter(c => c.duration_seconds && c.status === 'completed')
  if (completedCalls.length > 0) {
    stats.totalDuration = completedCalls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0)
    stats.averageDuration = Math.round(stats.totalDuration / completedCalls.length)
  }

  return { data: stats, error: null }
}

// ============================================
// COMBINED HELPERS
// ============================================

/**
 * Get a work order with all related data
 * @param {string} workOrderId - Work order UUID
 * @returns {Promise<{data, error}>}
 */
export const getWorkOrderWithDetails = async (workOrderId) => {
  const { data: workOrder, error: woError } = await getWorkOrder(workOrderId)
  if (woError || !workOrder) return { data: null, error: woError }

  // Get related phone calls
  const { data: callLogs } = await getPhoneCallLogsByWorkOrder(workOrderId)

  // Get related chat sessions
  let chatSessions = []
  if (workOrder.chat_sessions) {
    chatSessions = workOrder.chat_sessions
  }

  return {
    data: {
      ...workOrder,
      phoneCallLogs: callLogs || [],
      chatSessions: chatSessions || []
    },
    error: null
  }
}

/**
 * Get a chat session with all messages
 * @param {string} sessionId - Chat session UUID
 * @returns {Promise<{data, error}>}
 */
export const getSessionWithMessages = async (sessionId) => {
  const { data: session, error: sessionError } = await getChatSession(sessionId)
  if (sessionError || !session) return { data: null, error: sessionError }

  const { data: messages, error: messageError } = await getChatMessages(sessionId)
  if (messageError) return { data: null, error: messageError }

  // Get related phone calls
  const { data: callLogs } = await getPhoneCallLogsBySession(sessionId)

  return {
    data: {
      ...session,
      messages: messages || [],
      phoneCallLogs: callLogs || []
    },
    error: null
  }
}
